<template>
  <div>
    <AppInputLabel v-if="label" :name="name" :label="label" :hasError="hasError(name).value" :required="required ?? false">
    </AppInputLabel>
    <div class="select-wrapper" :class="{ 'ios-select': isIOS }">
      <select class="w-full" :class="{ 'border-critial': hasError(name).value }" v-model="innerValue"
        :disabled="disabled" @change="handleInput" @blur="validateInput">
        <option value="" disabled v-if="placeholder">{{ placeholder }}</option>

        <template v-if="isOptGroup">
          <optgroup v-for="(groupedItems, optGroupName, index) in (innerOptions as Record<string, SelectOption[]>)"
            :label="optGroupName" :key="`opt-${index}-${optGroupName}`">
            <option v-for="(item, index) in groupedItems" :value="item.id" :key="`item-${index}-${item.id}`"
              :selected="item.id === innerValue">
              {{ item.name }}
            </option>
          </optgroup>
        </template>
        <template v-else>
          <option v-for="(item, index) in (innerOptions as SelectOption[])" :key="index" :value="item.id"
            :selected="item.id === innerValue">
            {{ item.name }}
          </option>
        </template>
      </select>
    </div>

    <AppInputError v-show="hasError(name)" :error="errors.get(name)" />

  </div>
</template>

<script setup lang="ts">
import { inject, ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { ValidationContextKey } from "@/models/Context";
import useFormValidation from "@/modules/useFormValidation";
import { SelectOption } from "@/models/SelectOption";
import AppInputLabel from "./AppInputLabel.vue";
import AppInputError from "./AppInputError.vue";
import { sortUtils } from '@/utils/sortUtils';
import useDeviceDetection from "@/modules/useDeviceDetection";

const props = defineProps<{
  required?: boolean;
  value?: any;
  label?: string;
  name: string;
  placeholder?: string;
  options: SelectOption[] | Record<string, SelectOption[]>;
  disableAutoSet?: boolean;
  modelValue?: any;
  inline?: boolean;
  hideTooltip?: boolean;
  tooltipDisplayPosition?: string | null;
  disableSorting?: boolean;
  disabled?: boolean;
}>();
const isOptGroup: boolean = !Array.isArray(props.options);
const isLoading = ref(true);

// Detect iOS platform
const [Platform] = useDeviceDetection();
const isIOS = computed(() => Platform.value === "ios");

const getInnerValue = () => {
  if (!props.disableAutoSet) {
    // Check if options is an array or a record
    if (Array.isArray(props.options)) {
      // If it's an array and has elements, use the first element's id
      if (props.options.length > 0) {
        return props.modelValue ?? props.options[0].id;
      }
    } else {
      // If it's a record, get the first key's first element's id
      const firstKey = Object.keys(props.options)[0];
      if (firstKey && props.options[firstKey].length > 0) {
        return props.modelValue ?? props.options[firstKey][0].id;
      }
    }
  }
  // Default to modelValue or undefined
  return props.modelValue ?? "";
};

const innerValue = ref(getInnerValue());

const emit = defineEmits([
  "update:modelValue",
  "change",
]);

// Create a default validation context if none is provided
const defaultContext = {
  errors: new Map<string, string>(),
  validators: {} as Record<string, (key: string, validator: string) => void>,
  registerValidator: (key: string, validator: (key: string, validator: string) => void) => {
    defaultContext.validators[key] = validator;
  }
};

const context = inject(ValidationContextKey, defaultContext);

const { validateRequiredField, errors, clearValidator, hasError, clearError } = useFormValidation(
  context
);

const validateInput = () => {
  if (props.required && !isLoading.value) {
    // Fix validation to properly handle string values
    // Empty string, null, undefined, or the string "Select" should fail validation
    let validateValue = innerValue.value === "" || innerValue.value === null || innerValue.value === undefined ? "" : String(innerValue.value);
    validateRequiredField(props.name, props.label || props.name, validateValue);
  }
};

// Register the validator
context.registerValidator(props.name, validateInput);

const innerOptions = computed(() => {
  if ((props.disableSorting === null || !props.disableSorting) && props.options) {
    if (isOptGroup) {
      const optionRecords = props.options as Record<string, SelectOption[]>;
      Object.keys(optionRecords).forEach((x) => {
        optionRecords[x as string].sort((a, b) =>
          a.name && b.name ? a.name.localeCompare(b.name) : 0
        );
      });
      return optionRecords;
    }

    const options = [...(props.options as SelectOption[])];
    options.sort(sortUtils.byName);
    return options;
  }
  return props.options;
});

watch(
  () => props.options,
  (newOptions) => {
    let newInnerValue;

    // Check if newOptions is an array or a record
    if (Array.isArray(newOptions)) {
      // If it's an array and has elements, use the first element's id
      if (newOptions.length > 0 && !props.disableAutoSet) {
        newInnerValue = props.modelValue ?? newOptions[0].id;
      }
    } else {
      // If it's a record, get the first key's first element's id
      const firstKey = Object.keys(newOptions)[0];
      if (firstKey && newOptions[firstKey].length > 0 && !props.disableAutoSet) {
        newInnerValue = props.modelValue ?? newOptions[firstKey][0].id;
      }
    }

    // Update innerValue
    innerValue.value = newInnerValue ?? props.modelValue ?? "";
  },
  { immediate: true }
);

watch(
  () => props.modelValue,
  (newValue) => {
    if (innerValue.value === newValue) return;
    innerValue.value = newValue ?? "";
    validateInput();
  },
  { immediate: true }
);

const emitModelValueUpdate = (value: any) => {
  // Handle special cases that should return null
  if (value === "" || value < 0 || value === "-1" || value === "Not Selected") {
    emit("update:modelValue", null);
    return;
  }

  // Check if the value is a numeric string and should be converted to a number
  if (typeof value === 'string' && !isNaN(Number(value)) && value.trim() !== '') {
    emit("update:modelValue", Number(value));
  } else {
    emit("update:modelValue", value);
  }
};

const handleInput = (event: Event) => {
  validateInput();
  const target = event.target as HTMLSelectElement;
  const value = target.value;
  emitModelValueUpdate(value);
  emit("change", value);
};

onMounted(() => {
  isLoading.value = false;
});
onBeforeUnmount(() => {
  clearValidator(props.name);
  clearError(props.name);
});
</script>

<style scoped>
.select-wrapper {
  position: relative;
  width: 100%;
}

.ios-select {
  position: relative;
}

.ios-select::after {
  content: '';
  position: absolute;
  right: 12px;
  top: 50%;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid rgba(0, 0, 0, 0.5);
  transform: translateY(-50%);
  pointer-events: none;
}

.ios-select select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  padding-right: 30px; /* Make room for the custom arrow */
}
</style>
